import { Controller } from "@hotwired/stimulus";

/**
 * Shows a "stalled" warning on an import step row when no progress has
 * been reported for longer than `thresholdMsValue`.
 *
 * Purely client-side — the server never marks a step stalled. On every
 * Turbo Stream replace the controller reconnects with a fresh
 * `lastProgressAtValue`, so a resumed step automatically clears the
 * warning without extra wiring.
 *
 * Values:
 *   - lastProgressAtValue: ISO-8601 timestamp of the step's most recent
 *     progress tick. Empty string = no ticks yet.
 *   - thresholdMsValue: ms before the step is considered stalled
 *     (default 60_000).
 *   - stateValue: the step's current state. Only `running` steps ever
 *     surface the warning.
 *
 * Targets:
 *   - warning: the badge element toggled via the `hidden` Tailwind class.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["warning"];
  static values = {
    lastProgressAt: String,
    thresholdMs: { type: Number, default: 60000 },
    state: String
  };

  declare hasWarningTarget: boolean;
  declare warningTarget: HTMLElement;
  declare lastProgressAtValue: string;
  declare thresholdMsValue: number;
  declare stateValue: string;

  private checkInterval: number | undefined;
  private isConnected = false;
  private readonly CHECK_EVERY_MS = 5000;

  connect() {
    this.isConnected = true;
    this.restartPolling();
  }

  disconnect() {
    this.isConnected = false;
    this.stopPolling();
  }

  lastProgressAtValueChanged() {
    if (!this.isConnected) return;

    this.updateWarning();
  }

  stateValueChanged() {
    if (!this.isConnected) return;

    this.restartPolling();
  }

  thresholdMsValueChanged() {
    if (!this.isConnected) return;

    this.updateWarning();
  }

  private restartPolling() {
    this.stopPolling();
    this.updateWarning();

    if (this.stateValue === "running") {
      this.checkInterval = window.setInterval(() => this.updateWarning(), this.CHECK_EVERY_MS);
    }
  }

  private stopPolling() {
    if (this.checkInterval) {
      window.clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  private updateWarning() {
    if (!this.hasWarningTarget) return;

    if (this.stateValue !== "running") {
      this.setWarningVisible(false);
      return;
    }

    this.setWarningVisible(this.isStalled());
  }

  private setWarningVisible(visible: boolean) {
    this.warningTarget.hidden = !visible;
    this.warningTarget.style.display = visible ? "" : "none";
    this.warningTarget.classList.toggle("hidden", !visible);
    this.warningTarget.classList.toggle("!hidden", !visible);
  }

  private isStalled(): boolean {
    if (!this.lastProgressAtValue) return false;

    const lastTs = Date.parse(this.lastProgressAtValue);
    if (Number.isNaN(lastTs)) return false;

    return Date.now() - lastTs > this.thresholdMsValue;
  }
}
